import React, { useEffect, useRef, useState } from 'react';
import { Search, X, Hash, MessageSquare, Lock } from 'lucide-react';
import { searchMessages } from '../../lib/db';

function useDebouncedValue(value, delay = 220) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
}

function timeAgo(ts) {
  if (!ts) return '';
  const diff = Date.now() - new Date(ts).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d ago`;
  return new Date(ts).toLocaleDateString();
}

/**
 * Cmd-K style overlay for searching across both team channels and portal
 * threads. Server-side ranking; uses `<mark>` tags from ts_headline so we
 * can render highlights without a second parser.
 *
 * Calls onSelect({ scope, container_id, message_id }) when the user picks
 * a result; the parent is responsible for navigating to and (eventually)
 * scrolling that message into view.
 */
export default function MessageSearch({ open, onClose, onSelect }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const inputRef = useRef(null);
  const debounced = useDebouncedValue(query, 220);

  useEffect(() => {
    if (open) {
      // Focus the input next tick so any animation has time to settle.
      const t = setTimeout(() => inputRef.current?.focus(), 30);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    if (debounced.trim().length < 2) { setResults([]); return undefined; }
    let cancelled = false;
    setLoading(true);
    searchMessages(debounced, 40).then(({ data, error }) => {
      if (cancelled) return;
      setLoading(false);
      if (error) { setResults([]); return; }
      setResults(Array.isArray(data) ? data : []);
      setHighlight(0);
    });
    return () => { cancelled = true; };
  }, [debounced, open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') { onClose(); return; }
      if (!results.length) return;
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setHighlight((h) => Math.min(h + 1, results.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setHighlight((h) => Math.max(h - 1, 0));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        const r = results[highlight];
        if (r) onSelect?.(r);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, results, highlight, onClose, onSelect]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-slate-900/40 backdrop-blur-sm pt-[10vh] px-4" onClick={onClose}>
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-2xl overflow-hidden"
      >
        <div className="flex items-center gap-3 px-4 py-3 border-b border-slate-200 dark:border-slate-800">
          <Search size={18} className="text-slate-400 flex-shrink-0" />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search messages — type a phrase, &quot;exact quote&quot;, or word -exclude"
            className="flex-1 bg-transparent text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none"
          />
          <kbd className="hidden sm:inline-block px-1.5 py-0.5 rounded text-[10px] font-medium text-slate-500 bg-slate-100 dark:bg-slate-800">ESC</kbd>
          <button onClick={onClose} className="p-1 rounded text-slate-400 hover:text-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800">
            <X size={16} />
          </button>
        </div>

        <div className="max-h-[60vh] overflow-y-auto">
          {query.trim().length < 2 && (
            <div className="px-5 py-12 text-center text-slate-500">
              <Search size={28} className="mx-auto mb-2 opacity-40" />
              <p className="text-sm">Type at least 2 characters to search.</p>
              <p className="text-xs mt-1 text-slate-400">
                Searches every team channel and client thread you can see.
              </p>
            </div>
          )}

          {query.trim().length >= 2 && loading && (
            <div className="px-5 py-8 text-center text-sm text-slate-500">Searching…</div>
          )}

          {query.trim().length >= 2 && !loading && results.length === 0 && (
            <div className="px-5 py-10 text-center text-sm text-slate-500">No matches.</div>
          )}

          {results.length > 0 && (
            <ul className="py-1">
              {results.map((r, idx) => {
                const Icon = r.scope === 'firm' ? Hash : MessageSquare;
                return (
                  <li key={`${r.scope}-${r.message_id}`}>
                    <button
                      onClick={() => onSelect?.(r)}
                      onMouseEnter={() => setHighlight(idx)}
                      className={`w-full text-left px-4 py-2.5 transition flex items-start gap-3 ${
                        idx === highlight
                          ? 'bg-indigo-50 dark:bg-indigo-900/30'
                          : 'hover:bg-slate-50 dark:hover:bg-slate-800/50'
                      }`}
                    >
                      <div className="w-7 h-7 rounded-lg bg-slate-100 dark:bg-slate-800 flex items-center justify-center flex-shrink-0 text-slate-500 mt-0.5">
                        <Icon size={13} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5 text-xs">
                          <span className="font-semibold text-slate-700 dark:text-slate-200 truncate">
                            {r.scope === 'firm' ? '#' : ''}{r.container_label || 'untitled'}
                          </span>
                          {r.container_private && (
                            <Lock size={10} className="text-amber-500 flex-shrink-0" />
                          )}
                          <span className="text-slate-400">·</span>
                          <span className="text-slate-500 truncate">{r.actor_label}</span>
                          <span className="text-slate-400">·</span>
                          <span className="text-slate-400 flex-shrink-0">{timeAgo(r.created_at)}</span>
                        </div>
                        <p
                          className="mt-0.5 text-sm text-slate-700 dark:text-slate-200 line-clamp-2 [&_mark]:bg-amber-200 dark:[&_mark]:bg-amber-500/30 [&_mark]:text-amber-900 dark:[&_mark]:text-amber-100 [&_mark]:rounded-sm [&_mark]:px-0.5"
                          dangerouslySetInnerHTML={{ __html: r.snippet || '' }}
                        />
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="px-4 py-2 border-t border-slate-200 dark:border-slate-800 text-[11px] text-slate-400 flex items-center justify-between">
          <span>↑↓ to navigate · ↵ to open · ESC to close</span>
          <span>{results.length > 0 ? `${results.length} results` : ''}</span>
        </div>
      </div>
    </div>
  );
}
